"use strict";

import {floorf, isset, roundf} from '../../js-utilities';
import stringConstants from "./string-constants";

// Inputs are in metric units.
export function weightFromHeightAndBmi(height, bmi) {
    return (bmi * Math.pow(height / 100, 2));
}

// Inputs are in metric units.
export function heightFromWeightAndBmi(weight, bmi) {
    return ((Math.sqrt(weight / bmi)) * 100);
}

// Inputs are in metric units.
export function bmiFromHeightAndWeight(height, weight) {
    return weight / Math.pow(height / 100, 2);
}

export function lbsToKgs(lbs) {
    return lbs / 2.20462262185;
}

export function kgsToLbs(kgs) {
    return kgs * 2.20462262185;
}

export function inchesToCms(inches) {
    return inches * 2.54;
}

export function cmsToInches(cms) {
    return cms / 2.54;
}

export function inchesToFeetAndInches(inches) {
    const feet = floorf(inches / 12, 0);
    const remainingInches = inches % 12;
    return `${feet}' ${remainingInches}"`;
}

export function parseInput(value, type) {
    let return_value;
    let input_value = NaN;
    if (type === 'int') {
        input_value = parseInt(value, 10);
    } else if (type === 'float') {
        input_value = parseFloat(value);
    }
    if (!isNaN(input_value)) {
        return_value = input_value;
    }
    return return_value;
}

export function mapClientToServerValues(data) {
    return {
        // calculator: data.subdomain,
        gender: data.gender,
        birth_month: data.birth_month,
        birth_day: data.birth_day,
        birth_year: data.birth_year,
        ymd_age: data.ymd_age,
        fmd: data.fmd,
        smoking: data.smoking,
        vitamin_d: data.vitamin_d,
        systolic_blood_pressure: data.systolic_blood_pressure,
        diastolic_blood_pressure: data.diastolic_blood_pressure,
        blood_pressure_treated: data.blood_pressure_treated,
        height: data.height,
        weight: data.weight,
        bmi: data.bmi,
        waist: data.waist,
        a1c: data.a1c,
        lipids_tg: data.lipids_tg,
        lipids_hdlc: data.lipids_hdlc,
        lipids_ldlc: data.lipids_ldlc,
        underlying_kidney: data.underlying_kidney,
        underlying_chronic_lung: data.underlying_chronic_lung,
        underlying_cardio: data.underlying_cardio,
        underlying_immuno: data.underlying_immuno,
        hospital: data.hospital,
        icu: data.icu,
        death: data.death,
        vac_year: data.vac_year,
        vac_month: data.vac_month,
        vac_day: data.vac_day,
        imperial: data.imperial ? 1 : 0,
    };
}

export function mapServerToClientValues(data) {
    const result = {
        // subdomain: data.calculator,
        gender: data.gender,
        birth_month: data.birth_month,
        birth_day: data.birth_day,
        birth_year: data.birth_year,
        ymd_age: data.birth_date,
        fmd: data.angiodefender,
        smoking: data.smoking,
        vitamin_d: data.vitamin_d,
        systolic_blood_pressure: data.blood_pressure_systolic,
        diastolic_blood_pressure: data.blood_pressure_diastolic,
        blood_pressure_treated: data.blood_pressure_treated,
        bmi: data.bmi,
        waist: data.waist_circumference,
        a1c: data.hba1c,
        lipids_tg: data.lipids_tg,
        lipids_hdlc: data.lipids_hdlc,
        lipids_ldlc: data.lipids_ldlc,
        underlying_kidney: data.chronic_kidney,
        underlying_chronic_lung: data.chronic_lung,
        underlying_cardio: data.cardiovascular_disease,
        underlying_immuno: data.immuno_condition,
        hospital: data.hospitalization,
        icu: data.icu,
        death: data.death,
        vac_year: data.vascular_age,
        vac_month: data.vac_month,
        vac_day: data.vac_day,
        imperial: data.imperial ? true : false,
    };
    // Vascular age is combined to 00y 00m 00d on the server, so break it back apart when loading user data
    if (data.vascular_age) {
        const vacAgeRegex = /(\d*)y (\d*)m (\d*)d/i;
        const vacAgeMatch = data.vascular_age.match(vacAgeRegex);
        if (vacAgeMatch) {
            const vac_year = parseFloat(vacAgeMatch[1]);
            const vac_month = parseFloat(vacAgeMatch[2]);
            const vac_day = parseFloat(vacAgeMatch[3]);
            if (!isNaN(vac_year)) {
                result.vac_year = vac_year;
            }
            if (!isNaN(vac_month)) {
                result.vac_month = vac_month;
            }
            if (!isNaN(vac_day)) {
                result.vac_day = vac_day;
            }
        }
    }
    const height = parseFloat(data.height);
    if (!isNaN(height)) {
        result.height = height;
    }
    const weight = parseFloat(data.weight);
    if (!isNaN(weight)) {
        result.weight = weight;
    }
    return result;
}

export class ManagerEvent extends Event {
    constructor(type, eventInitDictOrPayload, payload) {
        if (eventInitDictOrPayload
            && (eventInitDictOrPayload.bubbles !== undefined
                || eventInitDictOrPayload.cancelable !== undefined
                || eventInitDictOrPayload.composed !== undefined)) {
            super(type, eventInitDictOrPayload);
            this.payload = payload;
        } else {
            super(type);
            this.payload = eventInitDictOrPayload || payload;
        }
    }
}

export function generateValueOutput(propertyName, response, controller) {
    // Shouldn't be passing in the whole controller here, but it is easier for now, can refactor in the future
    const currentValue = controller.current_values[propertyName];
    const constraint = controller.constraints[propertyName];
    const precision = (typeof constraint.precision === 'function' ? constraint.precision(controller.current_values) : constraint.precision) || 0;
    const imperialConversion = constraint.imperialConversion;

    let summary = '';
    let summaryValue;
    let outputCellLabel;
    let outputCellValue = stringConstants.no_output_value_string;
    let outputCellColor = stringConstants.result_gray;
    let outputCellIcon = '../images/question-mark.png';

    let ageChange = undefined;
    let ageChangeText = undefined;

    if (!isset(currentValue)) {
        summaryValue = controller.language[`${propertyName}_output_none`];
        outputCellLabel = controller.language[`${propertyName}_output_none`];
    } else {
        const imperial = controller.current_values.imperial;
        const outValue = roundf(currentValue / ((imperial && imperialConversion) ? constraint.imperialConversion : 1), precision);
        const units = controller.language[`${propertyName}_output_units${imperialConversion ? (imperial ? '_imperial' : '_metric') : ''}`];
        summaryValue = controller.language[`${propertyName}_output_label`] + `${outValue}${units}`;
        outputCellLabel = controller.language[`${propertyName}_output_label`] + `${outValue}${units}`;
    }

    if (isset(response) && response !== -1) {
        ageChange = roundf(response.age_change, 6);
        ageChangeText = controller.text_for_age_change(ageChange);
        outputCellValue = ageChangeText;
        outputCellColor = controller.color_for_age_change(controller.current_values.fractional_age, ageChange);
        outputCellIcon = controller.icon_for_age_change(controller.current_values.fractional_age, ageChange);
    }

    summary = outputCellValue

    return {
        currentValue,
        ageChange,
        ageChangeText,
        summary,
        summaryValue,
        outputCellLabel,
        outputCellValue,
        outputCellColor,
        outputCellIcon
    };
}

export function parseBoolean(input) {
    if (input === true || input === 'true' || input === 1 || input === '1') {
        return true;
    } else if (input === false || input === 'false' || input === 0 || input === '0') {
        return false;
    }
    return undefined;
}
